import { Link } from "react-router-dom";
import { useEffect, useState } from "react";

import { invokeEdgeFunction } from "@/lib/edge-functions";

type Row = {
  id: string;
  created_at: string;
  customer_email: string;
  amount_total_cents: number;
  currency: string;
  status: string;
  line_summary: string;
  hasExpert: boolean;
  hasPush: boolean;
  expertReviewStatus: string | null;
};

function expertBadge(status: string | null) {
  if (!status) return <span className="text-xs text-zinc-400">—</span>;
  if (status === "awaiting_audio") return <span className="text-amber-700">Awaiting audio</span>;
  if (status === "audio_submitted") return <span className="text-violet-700">Audio in</span>;
  if (status === "writeup_ready") return <span className="text-emerald-700">Writeup ready</span>;
  return <span>{status}</span>;
}

export function AdminOrdersClient() {
  const [filter, setFilter] = useState<"all" | "expert" | "push">("expert");
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr("");
    invokeEdgeFunction<{ orders: Row[] }>("admin-orders", { filter })
      .then((j) => {
        if (cancelled) return;
        setRows(j.orders ?? []);
      })
      .catch(() => {
        if (!cancelled) setErr("Network error");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filter]);

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {(["expert", "push", "all"] as const).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`rounded-lg px-3 py-1.5 text-xs font-bold capitalize ${
              filter === f ? "bg-violet-600 text-white" : "bg-zinc-200 text-zinc-800 hover:bg-zinc-300"
            }`}
          >
            {f === "push" ? "Business push" : f}
          </button>
        ))}
      </div>

      {err ? <p className="mt-4 text-sm text-rose-600">{err}</p> : null}
      {loading ? <p className="mt-6 text-sm text-zinc-500">Loading…</p> : null}

      {!loading && rows.length === 0 ? (
        <p className="mt-6 text-sm text-zinc-600">No orders in this view.</p>
      ) : null}

      <ul className="mt-6 space-y-3">
        {rows.map((o) => (
          <li key={o.id} className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                  {new Date(o.created_at).toLocaleString()}
                </p>
                <p className="mt-1 font-bold text-zinc-900">{o.line_summary}</p>
                <p className="mt-1 text-xs text-zinc-600">{o.customer_email}</p>
                <div className="mt-2 flex flex-wrap gap-2 text-[11px] font-semibold">
                  {o.hasExpert ? (
                    <span className="rounded bg-violet-100 px-2 py-0.5 text-violet-900">Expert</span>
                  ) : null}
                  {o.hasPush ? (
                    <span className="rounded bg-sky-100 px-2 py-0.5 text-sky-900">Business push</span>
                  ) : null}
                </div>
              </div>
              <div className="text-right text-sm">
                <p className="font-black tabular-nums">${(o.amount_total_cents / 100).toFixed(2)}</p>
                <p className="mt-1 text-xs uppercase text-zinc-500">{o.status}</p>
                {o.hasExpert ? <p className="mt-2 text-xs font-bold">Expert: {expertBadge(o.expertReviewStatus)}</p> : null}
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-3 border-t border-zinc-100 pt-3">
              {o.hasExpert ? (
                <Link
                  to={`/admin/expert/${o.id}`}
                  className="text-sm font-bold text-violet-700 underline-offset-2 hover:underline"
                >
                  Open expert workspace →
                </Link>
              ) : null}
              {o.hasPush ? (
                <Link
                  to={`/admin/push/${o.id}`}
                  className="text-sm font-bold text-sky-800 underline-offset-2 hover:underline"
                >
                  Open push thread →
                </Link>
              ) : null}
              <a
                href={`mailto:${encodeURIComponent(o.customer_email)}`}
                className="text-sm font-bold text-zinc-600 underline-offset-2 hover:underline"
              >
                Email customer
              </a>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
